/**
 * The UK home-page block (bidmio.com): crew, doors and the two world pages.
 * Written for British trades, not translated: World A covers sole traders and
 * CIS subcontractors whose work and money come and go; World B covers firms
 * that are growing out of one van. Every answer repeats only what the English
 * pages already claim or the app does (quotes from the phone, deposit invoices,
 * reminders, receipts on the job, stock per van, leave calendar, job profit,
 * the 13-week cash forecast on Pro). Bidmio does no CIS, MTD or payroll in the UK.
 */
import { CREW_ORDER, type CrewEntry, type CrewKey, type World, type WorldPage } from "../crewData";
import type { QuoteMarks, WorldsContent } from "../worldsContent";

export const EN_QUOTES: QuoteMarks = ["“", "”"];

export const EN_WORLD_PAGES: Record<World, WorldPage> = {
  survive: {
    href: "/when-money-is-tight/",
    door: "That's me: the work and the money come and go",
    title: "When the work comes and goes, and so does the money",
  },
  grow: {
    href: "/when-the-business-grows/",
    door: "That's me: busy, growing, losing control",
    title: "Growing pains: more jobs, more people, less control",
  },
};

export const EN_CREW: Record<CrewKey, CrewEntry> = {
  mason: {
    label: "Brickie",
    home: "survive",
    survive: { quote: "Wages are due Friday. The money's not there. Again.", answer: "See who owes you and for how long. On Pro, a 13-week cash forecast shows the thin weeks coming." },
    grow: { quote: "Three sites on and I don't know where my lads are.", answer: "Everyone's day plan on their own phone. Nobody rings you at half six." },
  },
  carpenter: {
    label: "Chippy",
    home: "grow",
    survive: { quote: "Bought the timber. Not paid a penny yet.", answer: "Send a deposit invoice before you start. The customer pays for the timber, not you." },
    grow: { quote: "Booked solid till spring. Can't keep up.", answer: "Crews, jobs and dates in one place, without the phone calls." },
  },
  electrician: {
    label: "Sparky",
    home: "survive",
    survive: { quote: "Undercut by a third. By a bloke with no certs.", answer: "An itemised quote from your phone before you leave the house. Customers wary of cowboys pick the one who looks organised." },
    grow: { quote: "Where's 40 metres of twin and earth gone?", answer: "Every part booked out to the job it went into. You see where it went." },
  },
  plumber: {
    label: "Plumber",
    home: "grow",
    survive: { quote: "Burst pipe at 2am. Never invoiced it.", answer: "Log the call-out on your phone that night. In the morning it becomes the invoice." },
    grow: { quote: "Installs, call-outs, the lads. All in my head.", answer: "Installs and call-outs in one plan. Your head is free again." },
  },
  painter: {
    label: "Decorator",
    home: "survive",
    survive: { quote: "Two months I've been waiting to get paid.", answer: "Invoice the day you finish, see who owes you, and let reminders go out when it's overdue." },
    grow: { quote: "The crew's out painting and I couldn't tell you where.", answer: "Each painter sees their own job. You see all of them." },
  },
  roofer: {
    label: "Roofer",
    home: "grow",
    survive: { quote: "It's chucking it down, the lads are stood about, wages still go out.", answer: "See the hard weeks coming while there's still time to do something about them." },
    grow: { quote: "Two off sick and the roof's open.", answer: "Holiday and sick leave in one calendar. You see who's off and who to move." },
  },
  service: {
    label: "Boiler engineer",
    home: "survive",
    survive: { quote: "Nothing in the diary this week.", answer: "Every customer and every past job in one place. You know who to ring." },
    grow: { quote: "Six engineers and I'm the dispatcher.", answer: "Visits planned, service reports and invoices done from the phone." },
  },
  siteManager: {
    label: "Site manager",
    home: "grow",
    survive: { quote: "Main contractor's holding my retention. Again.", answer: "Every unpaid invoice in one list, with how long it's been overdue." },
    grow: { quote: "Turnover's up. Cash is down.", answer: "Profit on every job, not just turnover." },
  },
  owner: {
    label: "Owner",
    home: "grow",
    survive: { quote: "Client's paying late and I've got 30 wages to cover.", answer: "Who owes what, with reminders. You know who to ring first." },
    grow: { quote: "Forty on the books and I don't know which job makes money.", answer: "Profit on every job and who's working where today, in one place." },
  },
  tiler: {
    label: "Tiler",
    home: "survive",
    survive: { quote: "Customer changed the tiles. I paid the difference.", answer: "Price the change on the spot and get it approved before you start. It goes on the invoice." },
    grow: { quote: "Three flats on the go, materials everywhere.", answer: "Materials booked to each flat. You can see what went where." },
  },
  joiner: {
    label: "Kitchen fitter",
    home: "grow",
    survive: { quote: "Kitchen's in. The balance? Still waiting.", answer: "A deposit when they order, and the paid deposit comes off the final invoice." },
    grow: { quote: "Workshop, fitting, quotes. And there's one of me.", answer: "Workshop and fitting hours on every job." },
  },
  facade: {
    label: "Plasterer",
    home: "survive",
    survive: { quote: "Priced it under to win it. The van finance won't wait.", answer: "First look at who owes you. That money is already earned." },
    grow: { quote: "Scaffold's on the wrong job again.", answer: "Kit and plant logged to the job where they actually are." },
  },
  digger: {
    label: "Groundworker",
    home: "grow",
    survive: { quote: "Digger sat idle all week.", answer: "Empty weeks show up in the plan early, with time to ring round." },
    grow: { quote: "Nobody logged the digger hours.", answer: "Log the hours to the job from your phone, so they make it onto the invoice." },
  },
  developer: {
    label: "Small developer",
    home: "grow",
    grow: { quote: "Three schemes, ten subbies. The budget's running away.", answer: "Budget, changes and actual costs on every scheme. You see the overrun while it's happening, not at the end." },
  },
};

export const EN_WORLDS: WorldsContent = {
  lang: "en",
  quotes: EN_QUOTES,
  crew: EN_CREW,
  order: CREW_ORDER,
  pages: EN_WORLD_PAGES,
  doors: {
    survive: {
      quotes: [
        "Priced it under to win it. The van finance won't wait.",
        "Wages are due Friday. The money's not there. Again.",
        "‘Cash price, mate?’ Every other job.",
        "20% gone before it hits my account.",
      ],
      points: [
        "You take jobs below cost because the van finance and the wages won't wait.",
        "You've earned the money. It just hasn't arrived yet.",
      ],
    },
    grow: {
      quotes: [
        "Booked solid till spring. Can't keep up.",
        "Two off sick and the roof's open.",
        "Where's 40 metres of twin and earth gone?",
        "Turnover's up. Cash is down.",
      ],
      points: [
        "More people means more holidays, more sick days and more things going missing.",
        "Turnover is up, and somehow there's less in the bank.",
      ],
    },
  },
  ui: {
    heading: "Which one is you?",
    subheading: "Tap the one who sounds most like you, and see where to go next.",
    lineupAria: "Pick the one who sounds most like you",
    doorsBadge: "Let's be honest",
    doorsTitle: "Where are you today?",
    doorsIntro:
      "Plenty of people who work with their hands live month to month. Others have so much work they can't keep up. Both are hard, just in different ways.",
    doorLabel: "Door",
    doorCta: "That's me",
    doorsFooter: "Somewhere in between? Have a look at both.",
  },
};
